import { CashFlow, CashFlowType, ExpenseGroup } from '@api/CashFlows/CashFlow';

// ============================================================================
// ¿ESTE MOVIMIENTO CUENTA? — la única lectura (como `getOrderPaidBy` para pagos).
//
// Un movimiento de `cash-flows` responde DOS preguntas distintas
// (PLAN-GASTOS-Y-ADMIN-DE-SUCURSAL §3.2):
//   · ¿resta/suma en el corte de caja?  → `countsInCashCount` (salió o entró efectivo)
//   · ¿entra al Estado de Resultados?   → `countsInIncomeStatement` (es gasto del negocio)
// La renta por transferencia entra al ER y no toca la caja; la propina entregada en
// efectivo sale de la caja y no es gasto.
//
// Nadie filtra `flow.type === CashFlowType.X` por su cuenta: `cashFlowReaders.test.ts`
// lo fija leyendo el fuente. Un gasto cancelado deja de contar en TODOS lados de una vez.
// ============================================================================

/** Ausente = activo. Cancelar un gasto lo marca; nunca se borra. */
export const isActiveFlow = (flow: CashFlow): boolean => flow.status !== 'deleted';

/**
 * El efectivo automático de un pedido (`Input` con `orderId`). No es "otro ingreso":
 * ese dinero ya está en las ventas en efectivo, y contarlo aquí lo duplicaría.
 */
export const isOrderCash = (flow: CashFlow): boolean => flow.type === CashFlowType.Input && !!flow.orderId;

/**
 * ¿Movió efectivo de la caja? Activo, `paidFromCash` no apagado (ausente = sí) y
 * confirmado: un fijo estimado por el aseo no resta hasta que alguien lo confirma,
 * porque hasta entonces nadie sacó ese dinero (plan de Gastos §8.5).
 */
export const countsInCashCount = (flow: CashFlow): boolean =>
    isActiveFlow(flow) && flow.paidFromCash !== false && !flow.pendingConfirmation;

/** Otro ingreso de caja (no el de un pedido). */
export const isCashOtherInput = (flow: CashFlow): boolean =>
    countsInCashCount(flow) && flow.type === CashFlowType.Input && !flow.orderId;

/** Gasto que salió de la caja (sin envíos). */
export const isCashOutput = (flow: CashFlow): boolean => countsInCashCount(flow) && flow.type === CashFlowType.Output;

/** Gasto de envío que salió de la caja. */
export const isCashShipping = (flow: CashFlow): boolean => countsInCashCount(flow) && flow.type === CashFlowType.Shipping;

/** ¿Es un gasto del negocio para el Estado de Resultados? Salga o no de la caja. */
export const countsInIncomeStatement = (flow: CashFlow): boolean =>
    isActiveFlow(flow) &&
    (flow.type === CashFlowType.Output || flow.type === CashFlowType.Shipping) &&
    flow.categoryGroup !== ExpenseGroup.OutsideStatement;

/** ¿Es un gasto (con o sin categoría, activo o cancelado)? Los ingresos no. */
export const isExpenseFlow = (flow: CashFlow): boolean =>
    flow.type === CashFlowType.Output || flow.type === CashFlowType.Shipping;

/** Llave de "sin categoría": los gastos capturados antes de F2. */
export const UNCATEGORIZED = 'uncategorized';

/** El envío histórico (`Shipping` sin categoría) es la categoría "Envío" de hoy. */
export const SHIPPING_CATEGORY_ID = 'operating-shipping';

/** El grupo del gasto; el envío histórico es operativo. `undefined` = sin categoría. */
export const expenseGroupOf = (flow: CashFlow): ExpenseGroup | undefined =>
    flow.categoryGroup || (flow.type === CashFlowType.Shipping ? ExpenseGroup.Operating : undefined);

/** La categoría del gasto para filtrar: la suya, "Envío" o "sin categoría". */
export const expenseCategoryKeyOf = (flow: CashFlow): string =>
    flow.categoryId || (flow.type === CashFlowType.Shipping ? SHIPPING_CATEGORY_ID : UNCATEGORIZED);

/** Qué movimientos de caja van en cada renglón del informe y del modal "Otros gastos". */
export const cashFlowsOfType = (flows: CashFlow[], type: CashFlowType): CashFlow[] => {
    switch (type) {
        case CashFlowType.Input:
            return flows.filter(isCashOtherInput);
        case CashFlowType.Output:
            return flows.filter(isCashOutput);
        case CashFlowType.Shipping:
            return flows.filter(isCashShipping);
        default:
            return [];
    }
};

export const sumAmounts = (flows: CashFlow[]): number => flows.reduce((total, flow) => total + (Number(flow.amount) || 0), 0);
